/**
 * Manages sort popover functionality:
 * state management, user interaction tracking, and popover behavior controls.
 */
export const shouldShowSortPopover = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return true;
  }

  try {
    const popoverData = localStorage.getItem('sortByPopoverData');
    if (!popoverData) {
      return true;
    }

    const { dismissedByUser, usedDeliverySpeed, timestamp } = JSON.parse(popoverData);

    if (usedDeliverySpeed) {
      return false;
    }

    // Check 30-day period if user dismissed but hasn't used delivery speed
    if (dismissedByUser) {
      const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
      const timeRemaining = thirtyDaysInMs - (Date.now() - timestamp);

      if (timeRemaining > 0) {
        return false;
      }
      return true;
    }

    return true;
  } catch (error) {
    return true;
  }
};

// Updates the sort popover data in localStorage
export const updateSortPopoverData = (updates) => {
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      const existingData = localStorage.getItem('sortByPopoverData');
      const popoverData = existingData ? JSON.parse(existingData) : {};
      const updatedData = { ...popoverData, ...updates, timestamp: Date.now() };

      localStorage.setItem('sortByPopoverData', JSON.stringify(updatedData));
    } catch (error) {
      // Silently handle errors
    }
  }
};

export const isDeliverySpeedSort = (sortName) => {
  return sortName === 'Delivery Speed';
};

/**
 * Detects if the event target is the popover close button
 * It prevents popover from closing due to other user interactions like clicking away
 * This is a temporary solution until close button identification is standardized
 * across all popover implementations in the design system.
 */
export const isPopoverCloseButton = (event) => {
  if (!event?.target) return false;

  // First check if we're inside a popover
  const isInPopover = event.target.closest('.sui-popover') || event.target.closest('[role="tooltip"]');
  if (!isInPopover) return false;

  return (
    // Handle SVG path/svg elements (the actual X icon)
    (event.target.tagName?.toLowerCase() === 'path' && event.target.closest('button'))
    || (event.target.tagName?.toLowerCase() === 'svg' && event.target.closest('button'))
    // Handle regular button elements with close indicators - but only within popover
    || (event.target.getAttribute('aria-label')?.toLowerCase().includes('close'))
    || (typeof event.target.className === 'string' && event.target.className.includes('close'))
    || event.target.closest('[aria-label*="close" i]')
    || event.target.closest('button[title*="close" i]')
    || event.target.closest('.sui-popover-close')
    || event.target.closest('[data-testid*="close"]')
  );
};

export const shouldRenderPopover = (popoverAnchorEl, pageType, channel, showDeliverySpeed) => {
  return popoverAnchorEl && pageType === 'browse' && channel === 'desktop' && showDeliverySpeed;
};

// Handles sort selection with optional popover-specific logic
export const handleSortSelection = (
  sortNameOrType,
  onSortChange,
  sortTypeConfig,
  isPopoverEnabled = false,
  sortByPosition = 'dropdown'
) => {
  const { sortTypes, currentPage, pageType } = sortTypeConfig;

  // Handle both sortName (string) and sortType (object) for backward compatibility
  let selectedType;
  if (typeof sortNameOrType === 'string') {
    // Legacy behavior: lookup by name
    selectedType = sortTypes.find(type => type.name === sortNameOrType);
  } else if (sortNameOrType && typeof sortNameOrType === 'object') {
    // New behavior: use the sortType object directly
    selectedType = sortNameOrType;
  }

  if (!selectedType) {
    return { hidePopover: false };
  }

  onSortChange({
    sortby: selectedType.sortby,
    sortorder: selectedType.sortorder,
    name: selectedType.name,
    currentPage,
    page: pageType,
    sortByPosition
  });

  window.LIFE_CYCLE_EVENT_BUS.trigger('sortorder.value', {
    sortby: selectedType.sortby,
    sortorder: selectedType.sortorder
  });

  // Only execute popover-specific logic when popover is enabled
  if (isPopoverEnabled && isDeliverySpeedSort(selectedType.name)) {
    updateSortPopoverData({ usedDeliverySpeed: true });
    return { hidePopover: true };
  }

  return { hidePopover: false };
};

export const handlePopoverCloseEvent = (event, sortByTextRef, onClose) => {
  // Prevent close if clicking on anchor element (sort button)
  if (sortByTextRef.current && sortByTextRef.current.contains(event.target)) {
    return undefined;
  }

  const isCloseButton = isPopoverCloseButton(event);

  if (isCloseButton) {
    updateSortPopoverData({ dismissedByUser: true });
    onClose({ closePopover: true, resetInteraction: true });
  }
  // Static behavior - don't close unless explicitly requested
  return undefined;
};